import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

const ANIMATION_OPTIONS = {
  duration: 500,
};

class MarqueeComponent extends Component {
  requiredRefs = ['wrapper', 'content'];

  /**
   * @type {{ cancel: () => void, current: number } | null}
   */
  #animation = null;

  /**
   * @type {Node[]}
   */
  #originalItems = [];

  /**
   * @type {number | null}
   */
  #marqueeWidth = null;

  async connectedCallback() {
    super.connectedCallback();

    const { content } = this.refs;

    this.#originalItems = Array.from(content.children).map((item) => item.cloneNode(true));

    if (this.#originalItems.length === 0) return;

    await this.#waitForImages();
    await this.#buildMarquee();

    window.addEventListener('resize', this.#handleResize);
    this.addEventListener('pointerenter', this.#slowDown);
    this.addEventListener('pointerleave', this.#speedUp);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    window.removeEventListener('resize', this.#handleResize);
    this.removeEventListener('pointerenter', this.#slowDown);
    this.removeEventListener('pointerleave', this.#speedUp);
  }

  get clonedContent() {
    const { content, wrapper } = this.refs;
    const lastChild = wrapper.lastElementChild;

    return content !== lastChild ? lastChild : null;
  }

  #waitForImages() {
    const images = Array.from(this.querySelectorAll('img'));

    return Promise.all(
      images.map((image) => {
        if (image.complete) return Promise.resolve();

        return new Promise((resolve) => {
          image.addEventListener('load', resolve, { once: true });
          image.addEventListener('error', resolve, { once: true });
        });
      })
    );
  }

  async #buildMarquee() {
    const { content } = this.refs;

    this.clonedContent?.remove();

    content.replaceChildren(...this.#originalItems.map((item) => item.cloneNode(true)));

    await new Promise((resolve) => requestAnimationFrame(resolve));

    const marqueeWidth = this.getBoundingClientRect().width;
    const contentWidth = content.scrollWidth;

    if (!marqueeWidth || !contentWidth) return;

    const numberOfCopies = Math.max(1, Math.ceil(marqueeWidth / contentWidth));

    for (let i = 1; i < numberOfCopies; i++) {
      for (const item of this.#originalItems) {
        content.appendChild(item.cloneNode(true));
      }
    }

    this.#duplicateContent();
    this.#setSpeed(this.#calculateSpeed(numberOfCopies));
    this.#restartAnimation();
  }

  #calculateSpeed(numberOfCopies) {
    const rawSpeedFactor = Number(this.getAttribute('data-speed-factor'));
    const speedFactor = Number.isFinite(rawSpeedFactor) && rawSpeedFactor > 0 ? rawSpeedFactor : 20;

    return Math.sqrt(numberOfCopies) * speedFactor;
  }

  #setSpeed(value) {
    this.style.setProperty('--marquee-speed', `${value}s`);
  }

  #duplicateContent() {
    this.clonedContent?.remove();

    const clone = /** @type {HTMLElement} */ (this.refs.content.cloneNode(true));

    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('ref');

    clone.querySelectorAll('[ref]').forEach((element) => {
      element.removeAttribute('ref');
    });

    this.refs.wrapper.appendChild(clone);
  }

  #restartAnimation() {
    const animations = this.refs.wrapper.getAnimations();

    requestAnimationFrame(() => {
      for (const animation of animations) {
        animation.currentTime = 0;
      }
    });
  }

  #handleResize = debounce(async () => {
    const currentWidth = this.getBoundingClientRect().width;

    if (this.#marqueeWidth === currentWidth) return;

    this.#marqueeWidth = currentWidth;

    await this.#buildMarquee();
  }, 250);

  #slowDown = debounce(() => {
    if (this.#animation) return;

    const animation = this.refs.wrapper.getAnimations()[0];

    if (!animation) return;

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from: animation.playbackRate,
      to: 0,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }, ANIMATION_OPTIONS.duration);

  #speedUp = () => {
    this.#slowDown.cancel();

    const animation = this.refs.wrapper.getAnimations()[0];

    if (!animation || animation.playbackRate === 1) return;

    const from = this.#animation?.current ?? animation.playbackRate;
    this.#animation?.cancel();

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from,
      to: 1,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  };
}

function animateValue({ from, to, duration, onUpdate, easing = (t) => t * t * (3 - 2 * t), onComplete }) {
  const startTime = performance.now();
  let cancelled = false;
  let currentValue = from;

  function animate(currentTime) {
    if (cancelled) return;

    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easing(progress);

    currentValue = from + (to - from) * easedProgress;

    onUpdate(currentValue);

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else if (typeof onComplete === 'function') {
      onComplete();
    }
  }

  requestAnimationFrame(animate);

  return {
    get current() {
      return currentValue;
    },
    cancel() {
      cancelled = true;
    },
  };
}

if (!customElements.get('marquee-component')) {
  customElements.define('marquee-component', MarqueeComponent);
}